import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';
import './AdminMaintenance.css';

function AdminMaintenance() {
    const [maintenance, setMaintenance] = useState({
        maintenance_mode: false,
        maintenance_message: 'ระบบปิดปรับปรุงชั่วคราว กรุณากลับมาใหม่ภายหลัง',
        maintenance_start: null,
        maintenance_end: null,
        maintenance_scheduled: false,
        status: 'normal'
    });
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Schedule form
    const [scheduleForm, setScheduleForm] = useState({
        start_time: '',
        end_time: '',
        message: ''
    });
    const [scheduling, setScheduling] = useState(false);

    // Message form
    const [editMessage, setEditMessage] = useState('');

    const fetchMaintenance = useCallback(async () => {
        try {
            setLoading(true);
            const [maintenanceRes, historyRes] = await Promise.all([
                api.get('/admin/maintenance'),
                api.get('/admin/maintenance/history')
            ]);
            setMaintenance(maintenanceRes.data);
            setEditMessage(maintenanceRes.data.maintenance_message || '');
            setHistory(historyRes.data.history || []);
        } catch (err) {
            console.error('Error fetching maintenance:', err);
            setMessage({ type: 'error', text: 'ไม่สามารถโหลดข้อมูลได้' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMaintenance();
    }, [fetchMaintenance]);

    // Toggle maintenance mode
    const toggleMaintenance = async () => {
        const newState = !maintenance.maintenance_mode;
        const confirmMsg = newState
            ? 'ต้องการเปิดโหมดปิดปรับปรุงระบบใช่หรือไม่?\n\nผู้ใช้ทั่วไปจะไม่สามารถเข้าใช้งานได้จนกว่าจะปิดโหมดนี้'
            : 'ต้องการปิดโหมดปิดปรับปรุงและเปิดให้ใช้งานตามปกติใช่หรือไม่?';

        if (!window.confirm(confirmMsg)) return;

        try {
            setSaving(true);
            await api.put('/admin/maintenance', {
                enabled: newState,
                message: editMessage
            });
            setMessage({
                type: 'success',
                text: newState ? 'เปิดโหมดปิดปรับปรุงแล้ว' : 'ปิดโหมดปิดปรับปรุง กลับสู่สถานะปกติ'
            });
            fetchMaintenance();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    // Update message only
    const updateMessage = async () => {
        try {
            setSaving(true);
            await api.put('/admin/maintenance', {
                enabled: maintenance.maintenance_mode,
                message: editMessage
            });
            setMessage({ type: 'success', text: 'บันทึกข้อความประกาศสำเร็จ' });
            fetchMaintenance();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    // Schedule maintenance
    const handleSchedule = async (e) => {
        e.preventDefault();
        if (!scheduleForm.start_time) {
            setMessage({ type: 'error', text: 'กรุณาระบุวันเวลาเริ่มต้น' });
            return;
        }

        try {
            setScheduling(true);
            await api.post('/admin/maintenance/schedule', {
                start_time: scheduleForm.start_time,
                end_time: scheduleForm.end_time || null,
                message: scheduleForm.message || editMessage
            });
            setMessage({ type: 'success', text: 'ตั้งเวลาปิดปรับปรุงสำเร็จ' });
            setScheduleForm({ start_time: '', end_time: '', message: '' });
            fetchMaintenance();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setScheduling(false);
        }
    };

    // Cancel scheduled maintenance
    const cancelSchedule = async () => {
        if (!window.confirm('ต้องการยกเลิกการตั้งเวลาปิดปรับปรุงใช่หรือไม่?')) return;

        try {
            setSaving(true);
            await api.delete('/admin/maintenance/schedule');
            setMessage({ type: 'success', text: 'ยกเลิกการตั้งเวลาสำเร็จ' });
            fetchMaintenance();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('th-TH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format date for input
    const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toISOString().slice(0, 16);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="status-badge danger">กำลังปิดปรับปรุง</span>;
            case 'scheduled':
                return <span className="status-badge warning">รอเวลา</span>;
            case 'completed':
                return <span className="status-badge success">เสร็จสิ้น</span>;
            default:
                return <span className="status-badge">ปกติ</span>;
        }
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="loading-spinner">กำลังโหลด...</div>
            </div>
        );
    }

    return (
        <div className="admin-page maintenance-page">
            <div className="admin-header">
                <div className="header-with-back">
                    <Link to="/admin" className="back-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </Link>
                    <div>
                        <h1>จัดการปิดปรับปรุงระบบ</h1>
                        <p>ควบคุมการเปิด-ปิดระบบและแจ้งประกาศผู้ใช้งาน</p>
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                    <button onClick={() => setMessage({ type: '', text: '' })} className="close-btn">&times;</button>
                </div>
            )}

            {/* Current Status */}
            <div className="status-overview">
                <div className="status-card">
                    <div className="status-icon">
                        {maintenance.status === 'active' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                            </svg>
                        ) : maintenance.status === 'scheduled' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                        )}
                    </div>
                    <div className="status-info">
                        <h3>สถานะปัจจุบัน</h3>
                        {getStatusBadge(maintenance.status)}
                    </div>
                </div>

                {maintenance.status === 'scheduled' && maintenance.maintenance_start && (
                    <div className="schedule-info-card">
                        <p>กำหนดเริ่ม: <strong>{formatDate(maintenance.maintenance_start)}</strong></p>
                        {maintenance.maintenance_end && (
                            <p>กำหนดสิ้นสุด: <strong>{formatDate(maintenance.maintenance_end)}</strong></p>
                        )}
                        <button onClick={cancelSchedule} disabled={saving} className="btn-cancel-schedule">
                            ยกเลิกการตั้งเวลา
                        </button>
                    </div>
                )}
            </div>

            {/* Control Section */}
            <div className="admin-card">
                <h2 className="card-title">การควบคุม</h2>

                <div className="control-section">
                    <div className="toggle-row">
                        <div className="toggle-info">
                            <h4>เปิดโหมดปิดปรับปรุงทันที</h4>
                            <p>เมื่อเปิด ผู้ใช้ทั่วไปจะไม่สามารถเข้าใช้งานระบบได้ (ยกเว้น Admin)</p>
                        </div>
                        <button
                            onClick={toggleMaintenance}
                            disabled={saving}
                            className={`toggle-btn ${maintenance.maintenance_mode ? 'active' : ''}`}
                        >
                            <span className="toggle-slider"></span>
                        </button>
                    </div>

                    <div className="message-section">
                        <label>ข้อความประกาศ:</label>
                        <textarea
                            value={editMessage}
                            onChange={(e) => setEditMessage(e.target.value)}
                            placeholder="ข้อความที่จะแสดงให้ผู้ใช้เห็นเมื่อระบบปิดปรับปรุง"
                            rows={3}
                        />
                        <button
                            onClick={updateMessage}
                            disabled={saving || editMessage === maintenance.maintenance_message}
                            className="btn-save-message"
                        >
                            {saving ? 'กำลังบันทึก...' : 'บันทึกข้อความ'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Schedule Section */}
            {!maintenance.maintenance_mode && !maintenance.maintenance_scheduled && (
                <div className="admin-card">
                    <h2 className="card-title">ตั้งเวลาล่วงหน้า</h2>

                    <form onSubmit={handleSchedule} className="schedule-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>วันเวลาเริ่มต้น *</label>
                                <input
                                    type="datetime-local"
                                    value={scheduleForm.start_time}
                                    onChange={(e) => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                                    min={formatDateForInput(new Date().toISOString())}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>วันเวลาสิ้นสุด (ไม่บังคับ)</label>
                                <input
                                    type="datetime-local"
                                    value={scheduleForm.end_time}
                                    onChange={(e) => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                                    min={scheduleForm.start_time || formatDateForInput(new Date().toISOString())}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>ข้อความประกาศ (ถ้าไม่ระบุจะใช้ข้อความด้านบน)</label>
                            <input
                                type="text"
                                value={scheduleForm.message}
                                onChange={(e) => setScheduleForm({...scheduleForm, message: e.target.value})}
                                placeholder={editMessage || 'ข้อความประกาศ...'}
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" disabled={scheduling} className="btn-primary">
                                {scheduling ? 'กำลังตั้งเวลา...' : 'ตั้งเวลาปิดปรับปรุง'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* History Section */}
            <div className="admin-card">
                <h2 className="card-title">ประวัติการปิดปรับปรุง</h2>

                {history.length === 0 ? (
                    <div className="empty-state">
                        <p>ยังไม่มีประวัติการปิดปรับปรุง</p>
                    </div>
                ) : (
                    <div className="history-table-container">
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>วันเวลาเริ่ม</th>
                                    <th>วันเวลาสิ้นสุด</th>
                                    <th>ข้อความ</th>
                                    <th>ผู้ดำเนินการ</th>
                                    <th>สถานะ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((item) => (
                                    <tr key={item.id}>
                                        <td>{formatDate(item.start_time)}</td>
                                        <td>{formatDate(item.end_time)}</td>
                                        <td className="message-cell">{item.message || '-'}</td>
                                        <td>{item.created_by_name || 'ระบบ'}</td>
                                        <td>{getStatusBadge(item.status)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminMaintenance;
